import React, { useState } from 'react';
import { useSubscriptionStore, useAuthStore, useUIStore } from '../stores';
import { API_URL } from '../config';
import {
  Check,
  X,
  Sparkles,
  CreditCard,
  Calendar,
  Download,
  ArrowRight,
  Loader2,
  Zap,
  Crown,
} from 'lucide-react';

const plans = [
  {
    id: 'free',
    name: 'Free',
    price: { monthly: 0, yearly: 0 },
    description: 'For individuals getting started',
    features: [
      { text: '2 social accounts', included: true },
      { text: '10 scheduled posts/month', included: true },
      { text: 'Basic analytics', included: true },
      { text: '5 AI credits/month', included: true },
      { text: 'Team members', included: false },
      { text: 'Advanced analytics', included: false },
      { text: 'Priority support', included: false },
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    price: { monthly: 12, yearly: 10 },
    description: 'For creators and small businesses',
    features: [
      { text: '4 social accounts', included: true },
      { text: '50 scheduled posts/month', included: true },
      { text: 'Standard analytics', included: true },
      { text: '50 AI credits/month', included: true },
      { text: 'Team members', included: false },
      { text: 'Advanced analytics', included: false },
      { text: 'Email support', included: true },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: { monthly: 29, yearly: 24 },
    description: 'For growing teams',
    popular: true,
    features: [
      { text: '7 social accounts', included: true },
      { text: '200 scheduled posts/month', included: true },
      { text: 'Advanced analytics', included: true },
      { text: '200 AI credits/month', included: true },
      { text: '3 team members', included: true },
      { text: 'Content calendar', included: true },
      { text: 'Priority support', included: true },
    ],
  },
  {
    id: 'business',
    name: 'Business',
    price: { monthly: 59, yearly: 49 },
    description: 'For agencies and large teams',
    features: [
      { text: '7 social accounts', included: true },
      { text: '500 scheduled posts/month', included: true },
      { text: 'Advanced analytics', included: true },
      { text: '500 AI credits/month', included: true },
      { text: '10 team members', included: true },
      { text: 'Custom branding', included: true },
      { text: 'Dedicated support', included: true },
    ],
  },
];

const invoices = [
  { id: '1', date: 'Mar 1, 2024', amount: '$29.00', status: 'Paid' },
  { id: '2', date: 'Feb 1, 2024', amount: '$29.00', status: 'Paid' },
  { id: '3', date: 'Jan 1, 2024', amount: '$29.00', status: 'Paid' },
];

export default function Billing() {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [isUpgrading, setIsUpgrading] = useState(null);
  
  const { plan: currentPlan, upgradePlan } = useSubscriptionStore();
  const { addToast } = useUIStore();
  
  const handleUpgrade = async (planId) => {
    if (planId === currentPlan) return;

    setIsUpgrading(planId);

    try {
      const { token } = useAuthStore.getState();
      const res = await fetch(`${API_URL}/billing/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan: planId, cycle: billingCycle }),
      });
      const data = await res.json();

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }

      addToast({ type: 'error', title: 'Checkout failed', message: data.detail || 'Could not start checkout.' });
    } catch (e) {
      addToast({ type: 'error', title: 'Error', message: e.message || 'Payment processing failed.' });
    }

    setIsUpgrading(null);
  };
  
  const yearlyDiscount = 17;
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Billing</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Manage your subscription and billing
        </p>
      </div>
      
      {/* Current plan */}
      <div className="card p-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center">
            {currentPlan === 'free' ? (
              <Zap className="w-6 h-6 text-brand-600" />
            ) : (
              <Crown className="w-6 h-6 text-brand-600" />
            )}
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white capitalize">
              {currentPlan} Plan
            </p>
            <p className="text-sm text-gray-500">
              {currentPlan === 'free' ? 'Upgrade for more features' : 'Your plan renews on Apr 1, 2024'}
            </p>
          </div>
        </div>
        {currentPlan !== 'free' && (
          <button className="btn-secondary text-sm">
            Manage Subscription
          </button>
        )}
      </div>
      
      {/* Billing cycle toggle */}
      <div className="flex items-center justify-center gap-4">
        <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900 dark:text-white' : 'text-gray-500'}`}>
          Monthly
        </span>
        <button
          onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
          className="relative w-14 h-7 bg-gray-200 dark:bg-gray-700 rounded-full transition-colors"
        >
          <div className={`absolute top-1 w-5 h-5 bg-brand-500 rounded-full transition-transform ${
            billingCycle === 'yearly' ? 'translate-x-8' : 'translate-x-1'
          }`} />
        </button>
        <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900 dark:text-white' : 'text-gray-500'}`}>
          Yearly
        </span>
        <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          Save {yearlyDiscount}%
        </span>
      </div>
      
      {/* Plans grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((plan) => {
          const price = plan.price[billingCycle];
          const isCurrentPlan = plan.id === currentPlan;
          const isUpgradingThis = isUpgrading === plan.id;
          
          return (
            <div 
              key={plan.id}
              className={`card p-5 relative ${plan.popular ? 'ring-2 ring-brand-500' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-brand-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}
              
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{plan.name}</h3>
                <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
              </div>
              
              <div className="text-center mb-6">
                <span className="text-4xl font-bold text-gray-900 dark:text-white">
                  ${price}
                </span>
                {price > 0 && (
                  <span className="text-gray-500">/mo</span>
                )}
                {billingCycle === 'yearly' && price > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Billed ${price * 12}/year
                  </p>
                )}
              </div>
              
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    {feature.included ? (
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <X className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                    )}
                    <span className={feature.included ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400'}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>
              
              <button
                onClick={() => handleUpgrade(plan.id)}
                disabled={isCurrentPlan || isUpgrading}
                className={`w-full py-2.5 rounded-lg font-medium transition-colors ${
                  isCurrentPlan
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 cursor-not-allowed'
                    : plan.popular
                      ? 'btn-primary'
                      : 'btn-secondary'
                }`}
              >
                {isUpgradingThis ? (
                  <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                ) : isCurrentPlan ? (
                  'Current Plan'
                ) : (
                  <>
                    {plan.id === 'free' ? 'Downgrade' : 'Upgrade'}
                    <ArrowRight className="w-4 h-4 ml-1 inline" />
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>
      
      {/* Payment method */}
      {currentPlan !== 'free' && (
        <div className="card p-5">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Payment Method</h3>
          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">•••• •••• •••• 4242</p>
                <p className="text-sm text-gray-500">Expires 12/25</p>
              </div>
            </div>
            <button className="btn-secondary text-sm">Update</button>
          </div>
        </div>
      )}
      
      {/* Invoices */}
      {currentPlan !== 'free' && (
        <div className="card">
          <div className="p-5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 dark:text-white">Billing History</h3>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-800">
            {invoices.map((invoice) => (
              <div key={invoice.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-900 dark:text-white">{invoice.date}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-medium text-gray-900 dark:text-white">{invoice.amount}</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                    {invoice.status}
                  </span>
                  <button className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400">
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
